import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';

export enum AssetApprovalStatus {
  PreApproved = 'PreApproved',
  PreApprovedInvalid = 'PreApprovedInvalid', // There was a pre-approval but either the Asset name is taken or the configuration group is invalid for the device.
  ApprovalRequired = 'ApprovalRequired',
  Conflict = 'Conflict',
}

export enum ApprovalAction {
  ReplaceDevice = 'ReplaceDevice',
  UpdateDeviceName = 'UpdateDeviceName',
  RegisterNewAsset = 'RegisterNewAsset',
  UpdatePreApproval = 'UpdatePreApproval',
}

export interface AssetApprovalBasicModel extends ModelWithId {
  ts: string; // timestamp
  an: string; // asset name
  did: string | null; // device ID
  cfgId: number | null; // configuration group ID
  by: string | null; // creator (device or user)
  sts: AssetApprovalStatus;
}

export interface AssetApprovalConflictItemModel extends ModelWithId {
  did: string | null; // device ID
  hw: AssetDeviceType;
  an: string; // asset name
  dn: string; // device name
  rep: boolean; // True if the asset is the reporting asset (i.e. the one to highlight)
}

export interface AssetApprovalActionModel extends ModelWithId {
  action: ApprovalAction;
  did: string | null; // device ID
  hw: AssetDeviceType | null;
  an: string; // asset name. min: 1, max: 8, warning if > 6, regex check on valid chars
  dn: string; // device name
  cfgId: number | null; // configuration group ID
  ags: number[]; // asset group IDs
  desc: string | null; // description
  regAsset: number | null; // database ID of the registered ID
}

export interface AssetApprovalModel extends ModelWithId {
  sts: AssetApprovalStatus;
  conflicts: AssetApprovalConflictItemModel[] | null;
  actions: AssetApprovalActionModel[];
}

export interface AssetProcessApprovalModel {
  newAsset?: {
    id: number; //The asset ID of the newly created asset.
    n: string; //The name of the newly created asset.
  };
}

export interface AssetApprovalActionRequest extends ModelWithId {
  payload: AssetApprovalActionModel;
}

export interface PreApprovalImportResponse {
  importFailures: {
    lineNumber: number; // the line number that was ignored (1-based)
    reason:
      | 'ExistingAsset'
      | 'MultipleMatchingApprovals'
      | 'ExistingApprovalNoChange'
      | 'InvalidData';
    // ExistingAsset - there was already an asset with this name
    // MultipleMatchingApprovals - there are multiple approvals in the table that match the name of the imported approval
    // ExistingApprovalNoChange - An existing approval was found, but no changes were made
    // InvalidData - Asset name was invalid
  }[];
}

export const assetApprovalsApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listApprovals: build.query<AssetApprovalBasicModel[], void>({
      query: () => 'api/assetapprovals',
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.AssetApproval, id: TagList }] : [],
      transformResponse: ({ items }: CollectionModel<AssetApprovalBasicModel>, meta, arg) => items,
    }),
    getApproval: build.query<AssetApprovalModel, ModelWithId>({
      query: ({ id }) => `api/assetapprovals/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.AssetApproval, id: id }] : [],
    }),
    processApproval: build.mutation<AssetProcessApprovalModel, AssetApprovalActionRequest>({
      query: ({ id, payload }) => ({
        url: `api/assetapprovals/${id}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => {
        const tags = [{ type: QueryTag.AssetApproval, id: TagList }];
        if (!result?.newAsset?.id) {
          tags.push({ type: QueryTag.AssetApproval, id: id.toString() });
        }
        if (result?.newAsset?.id) {
          tags.push(
            { type: QueryTag.AssetRegistered, id: TagList },
            { type: QueryTag.AssetRegistered, id: result.newAsset.id.toString() },
          );
        }
        return !error ? tags : [];
      },
    }),
    deleteApproval: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/assetapprovals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.AssetApproval, id: TagList },
              { type: QueryTag.AssetApproval, id: id },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListApprovalsQuery,
  useGetApprovalQuery,
  useProcessApprovalMutation,
  useDeleteApprovalMutation,
} = assetApprovalsApi;
